import { React, useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Stack,
  Button,
  Typography,
  Grid,
  Card,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Alert,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import ReactApexChart from 'react-apexcharts';
import { toast } from 'react-toastify';
import Services from 'src/services/Services';
import api from 'src/services/BaseService';
import HeatmapLegend from 'src/utils/mallManagement/HeatMapLegend';
import { useSelector } from 'react-redux';
import { SERVICES } from 'src/utils/servicePermissions';
import useServicePermission from 'src/hooks/useServicePermission';

const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_MAP_TR = { "monday": "P.tesi", "tuesday": "Salı", "wednesday": "Çarş.", "thursday": "Perş.", "friday": "Cuma", "saturday": "C.tesi", "sunday": "Pazar" };

export default function MallManagementCargo() {
  const theme = useTheme();
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const customer = useSelector((state) => state.customerReducer);

  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVideoModalOpen, setVideoModalOpen] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [showApplyBanner, setShowApplyBanner] = useState(false);
  const [openApplyDialog, setOpenApplyDialog] = useState(false);

  // Check service permission using hook
  const { hasPermission: cargoPermission, loading: permissionLoading } = useServicePermission(SERVICES.CARGO);

  const handleOpenVideoModal = () => setVideoModalOpen(true);
  const handleCloseVideoModal = () => setVideoModalOpen(false);

  const handleApplyClick = () => setOpenApplyDialog(true);
  const handleCloseDialog = () => setOpenApplyDialog(false);
  const handleConfirmApply = async () => {
    try {
      const mallID = localStorage.getItem('mallID');

      // Fetch mall information to get mall title
      const mallsResponse = await api.post('/malls/get-malls', { withOffices: true });

      if (mallsResponse.data && mallsResponse.data.status) {
        const foundMall = mallsResponse.data.result.find((item) => item.id === mallID);
        const mallTitle = foundMall?.title || 'Bilinmeyen AVM';

        // Prepare form data
        const formData = {
          mallName: mallTitle,
          mallID: mallID,
          userName: parsedUser?.name || parsedUser?.email || 'Bilinmeyen Kullanıcı',
          serviceName: 'Kargo',
        };

        // Send contact form
        const response = await Services.createContactForm(parsedUser.token, formData);

        if (response.data && response.data.status) {
          toast.success('Talebiniz başarıyla iletilmiştir.');
          handleCloseDialog();
        } else {
          toast.error(response.data?.message || 'Talep gönderilirken bir hata oluştu.');
        }
      } else {
        toast.error('AVM bilgileri alınamadı.');
      }
    } catch (error) {
      console.error('Error submitting service request:', error);
      toast.error('Talep gönderilirken bir hata oluştu.');
    }
  };

  useEffect(() => {
    if (permissionLoading) {
      return;
    }

    const fetchAllData = async () => {
      setLoading(true);
      try {
        // Use the permission from the hook
        const userHasPermission = cargoPermission;

        setHasPermission(userHasPermission);

        if (userHasPermission) {
          const token = localStorage.getItem('tokenForService');
          const mallID = localStorage.getItem("mallID");

          if (!token || !mallID) {
            toast.warn("Yetkilendirme anahtarı veya AVM ID bulunamadı.");
            setLoading(false);
            return;
          }
          const response = await Services.GetLiveAnalyticsCargo(token, mallID);
          if (response.data.status && response.data.result) {
            setAnalyticsData(response.data.result);
          } else {
            toast.error(response.data.message || 'Veriler alınamadı.');
            setAnalyticsData({});
          }
        } else {
          setAnalyticsData({});
          setShowApplyBanner(true);
        }
      } catch (error) {
        toast.error('Analitik verileri alınırken bir hata oluştu.');
        console.error("Analitik verileri alınırken hata oluştu:", error);
        setAnalyticsData({});
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [cargoPermission, permissionLoading]);

  const zeroOutSeries = (series) => {
    if (!Array.isArray(series)) return [];
    const firstEl = series[0];
    if (typeof firstEl === 'number') {
      return series.map(() => 0);
    }
    if (typeof firstEl === 'object' && firstEl !== null && 'data' in firstEl) {
      return series.map(s => {
        if (Array.isArray(s.data) && typeof s.data[0] === 'object' && 'y' in s.data[0]) {
          return { ...s, data: s.data.map(item => ({ ...item, y: 0 })) };
        }
        if (Array.isArray(s.data)) {
          return { ...s, data: s.data.map(() => 0) };
        }
        return { ...s, data: [] };
      });
    }
    return [];
  };

  const commonBarPlot = { bar: { columnWidth: '20%', borderRadius: 8, borderRadiusApplication: 'end' } };
  const deskBarPlot = { bar: { columnWidth: '40%', borderRadius: 8, borderRadiusApplication: 'end' } };

  const companyUsageData = analyticsData?.companyBasedMonthlyUsage ?? {};
  const companyUsageLabels = Object.keys(companyUsageData);
  const companyUsageSeries = Object.values(companyUsageData).map(company => company.total ?? 0);

  const companyUsageOptions = {
    chart: { type: 'donut', toolbar: { show: false } },
    labels: companyUsageLabels,
    colors: ['#1C64F2', '#E74694', '#FDBA8C', '#16BDCA'],
    plotOptions: { pie: { donut: { size: '65%' } } },
    dataLabels: { enabled: false },
    legend: { position: 'bottom', horizontalAlign: 'center' },
  };

  const loyaltyData = analyticsData?.userBaseMonthlyLoyalty ?? {};
  const loyaltySeries = [loyaltyData.old ?? 0, loyaltyData.new ?? 0];

  const loyaltyOptions = {
    chart: { type: 'donut', toolbar: { show: false } },
    labels: ['Düzenli Müşteri', 'Yeni Müşteri'],
    colors: ['#FDBA8C', '#1C64F2'],
    plotOptions: { pie: { donut: { size: '65%' } } },
    dataLabels: { enabled: false },
    legend: { position: 'bottom', horizontalAlign: 'center' },
  };

  const monthlyUsageData = analyticsData?.companyBasedMonthlyUsage ?? {};
  const aggregatedMonthly = {};
  for (const company in monthlyUsageData) {
    for (const week in monthlyUsageData[company]) {
      if (week !== 'total') {
        if (!aggregatedMonthly[week]) aggregatedMonthly[week] = { delivery: 0, post: 0 };
        aggregatedMonthly[week].delivery += monthlyUsageData[company][week].delivery ?? 0;
        aggregatedMonthly[week].post += monthlyUsageData[company][week].post ?? 0;
      }
    }
  }
  const monthlyCategories = Object.keys(aggregatedMonthly);
  const monthlyPostData = monthlyCategories.map(week => aggregatedMonthly[week].post);
  const monthlyStackSeries = [{ name: 'Gönderi', data: monthlyPostData }];
  const monthlyStackOptions = {
    chart: { type: 'bar', stacked: true, toolbar: { show: false } },
    plotOptions: commonBarPlot,
    stroke: { show: true, width: 2, colors: ['#fff'] },
    colors: ['#1C64F2', '#FDBA8C'],
    xaxis: { categories: monthlyCategories },
    yaxis: { title: { text: 'Toplam Kargo Adedi' } },
    legend: {
      position: 'bottom',
      showForSingleSeries: true
    },
    dataLabels: { enabled: false },
  };

  const weeklyUsageData = analyticsData?.companyBasedWeeklyUsage ?? {};
  const aggregatedWeekly = {};
  for (const company in weeklyUsageData) {
    for (const date in weeklyUsageData[company]) {
      if (!aggregatedWeekly[date]) aggregatedWeekly[date] = { delivery: 0, post: 0 };
      aggregatedWeekly[date].delivery += weeklyUsageData[company][date].delivery ?? 0;
      aggregatedWeekly[date].post += weeklyUsageData[company][date].post ?? 0;
    }
  }
  const weeklyCategories = Object.keys(aggregatedWeekly).sort((a, b) => new Date(a) - new Date(b));
  const weeklyPostData = weeklyCategories.map(date => aggregatedWeekly[date].post);
  const weeklyStackSeries = [{ name: 'Gönderi', data: weeklyPostData }];
  const weeklyStackOptions = {
    chart: { type: 'bar', stacked: true, toolbar: { show: false } },
    plotOptions: deskBarPlot,
    stroke: { show: true, width: 2, colors: ['#fff'] },
    colors: ['#1C64F2', '#FDBA8C'],
    xaxis: { categories: weeklyCategories },
    yaxis: { title: { text: 'Toplam Kargo Adedi' } },
    legend: {
      position: 'bottom',
      showForSingleSeries: true
    },
    dataLabels: { enabled: false },
  };

  // --- EKLENEN 13 AYLIK FİRMA BAZLI KULLANIM GRAFİĞİ ---
  const yearlyCompanyUsageChart = useMemo(() => {
    const companyData = analyticsData?.companyBasedYearlyUsage || {};
    
    // Geçmiş 13 ayı oluştur (Şimdiki aydan başlayıp 12 ay geriye)
    const last13Months = Array.from({ length: 13 }).map((_, i) => {
      const d = new Date();
      d.setMonth(d.getMonth() - (12 - i));
      const year = d.getFullYear();
      const monthStr = String(d.getMonth() + 1).padStart(2, '0');
      return `${year}-${monthStr}`;
    });

    // Ekranda okunabilir ay formatları oluştur "Oca 2026", "Şub 2026" vs.
    const categories = last13Months.map(monthStr => {
      const [year, month] = monthStr.split('-');
      const date = new Date(year, parseInt(month) - 1, 1);
      const mFormat = new Intl.DateTimeFormat('tr-TR', { month: 'short' }).format(date);
      return `${mFormat.charAt(0).toUpperCase() + mFormat.slice(1)} ${year}`;
    });

    const series = [];
    const companyColors = ['#1C64F2', '#16BDCA', '#FDBA8C', '#E74694', '#FACA15', '#31C48D'];
    let colorIndex = 0;
    const assignedColors = [];

    // Verideki şirketleri dolaş, 'false' olmayanları grafiğe ekle
    Object.entries(companyData).forEach(([companyKey, data]) => {
      if (data !== false && typeof data === 'object') {
        const formattedName = companyKey
          .split('-')
          .map(w => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
        
        // Şirketin 13 ay için değerlerini eşle, yoksa 0 ata
        const dataPoints = last13Months.map(m => data[m] || 0);
        
        series.push({
          name: formattedName,
          data: dataPoints
        });
        
        assignedColors.push(companyColors[colorIndex % companyColors.length]);
        colorIndex++;
      }
    });

    const options = {
      chart: { type: 'bar', stacked: true, toolbar: { show: false } },
      plotOptions: { bar: { columnWidth: '40%', borderRadius: 4 } },
      colors: assignedColors.length > 0 ? assignedColors : ['#1C64F2'],
      stroke: { show: true, width: 2, colors: ['transparent'] },
      xaxis: {
        categories: categories,
        labels: { rotate: -45, rotateAlways: false }
      },
      yaxis: {
        title: { text: 'Kullanım Adedi' },
        labels: { formatter: (val) => val.toFixed(0) }
      },
      legend: { position: 'bottom' },
      dataLabels: { enabled: false },
      tooltip: {
        y: { formatter: (val) => `${val} adet` }
      }
    };

    return { series, options };
  }, [analyticsData]);
  // ----------------------------------------------------

  const dailyHeatmapChart = useMemo(() => {
    const data = analyticsData?.dayHourlyUsageTotalCount ?? {};

    const allTimeSlots = Array.from({ length: 16 }, (_, i) => {
      const startHour = 10 + i;
      const endHour = 11 + i;
      const formattedStartHour = (startHour % 24).toString().padStart(2, '0');
      const formattedEndHour = (endHour % 24).toString().padStart(2, '0');
      return `${formattedStartHour}:00-${formattedEndHour}:00`;
    });

    // Her saat için satır toplamlarını hesapla
    const hourlyTotals = allTimeSlots.map(timeSlot => {
      return DAY_ORDER.reduce((sum, dayKey) => sum + (data[dayKey]?.[timeSlot] || 0), 0);
    });

    // Her gün için sütun toplamlarını hesapla
    const dailyTotals = DAY_ORDER.map(dayKey => {
      return allTimeSlots.reduce((sum, timeSlot) => sum + (data[dayKey]?.[timeSlot] || 0), 0);
    });

    // Genel toplam
    const grandTotal = dailyTotals.reduce((sum, val) => sum + val, 0);

    // En alta günlük toplamlar satırı ekle
    const totalRow = {
      name: 'Toplam',
      data: [
        ...DAY_ORDER.map((dayKey, index) => ({ x: DAY_MAP_TR[dayKey], y: dailyTotals[index] })),
        { x: 'Toplam', y: grandTotal }
      ]
    };

    // Series oluştur - her satıra "Toplam" sütunu ekle
    const series = [
      totalRow,
      ...allTimeSlots.map((timeSlot, index) => ({
        name: timeSlot.split('-')[0],
        data: [
          ...DAY_ORDER.map(dayKey => ({ x: DAY_MAP_TR[dayKey], y: data[dayKey]?.[timeSlot] || 0 })),
          { x: 'Toplam', y: hourlyTotals[index] }
        ]
      })).reverse()
    ];

    const allCounts = Object.values(data).flatMap(day => (day && typeof day === 'object') ? Object.values(day) : []);
    const maxCount = allCounts.length > 0 ? Math.max(...allCounts, 0) : 10;
    const colors = ['#D7D7D7', '#93D4F4', '#46A5F9', '#4086F5', '#345CC3'];
    const options = {
      chart: { type: 'heatmap', toolbar: { show: false } },
      plotOptions: {
        heatmap: {
          shadeIntensity: 0.5,
          radius: 2,
          colorScale: {
            ranges: [
              { from: 0, to: 0, color: colors[0] },
              { from: 1, to: Math.floor(maxCount * 0.25), color: colors[2] },
              { from: Math.floor(maxCount * 0.25) + 1, to: Math.floor(maxCount * 0.7), color: colors[3] },
              { from: Math.floor(maxCount * 0.7) + 1, to: maxCount, color: colors[4] }
            ]
          }
        }
      },
      dataLabels: {
        enabled: true,
        style: {
          colors: ['#000'],
          fontSize: '12px',
          fontWeight: 500
        }
      },
      stroke: { show: true, width: 3, colors: ['#fff'] },
      xaxis: { type: 'category' },
      legend: { show: false },
      tooltip: { y: { formatter: (value) => `${value} adet` } },
    };
    return { series, options, maxCount, colors };
  }, [analyticsData]);

  if (loading || permissionLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ px: 4, py: 3 }}>
      <Helmet>
        <title>Kargo</title>
      </Helmet>

      {showApplyBanner && !hasPermission && (
        <Stack sx={{ width: '100%', mb: 4, mx: 'auto', borderBottom: '1px solid #D6BBFB', borderRadius: 1, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
          <Alert
            severity="info"
            sx={{
              backgroundColor: 'white',
              alignItems: 'center',
              pr: 4,
              '& .MuiAlert-icon': {
                color: theme.palette.info.main
              }
            }}
            action={
              <Stack direction="row" gap={1} alignItems="center">
                <Button
                  color="primary"
                  variant="contained"
                  size="small"
                  onClick={handleApplyClick}
                  sx={{ padding: '10px 16px', cursor: 'pointer' }}
                >
                  Başvur
                </Button>
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    setShowApplyBanner(false);
                  }}
                >
                  <Iconify icon="eva:close-fill" width={20} height={20} />
                </IconButton>
              </Stack>
            }
            onClose={() => setShowApplyBanner(false)}
          >
            Bu hizmet şu anda aktif değil. Grafik ve liste verilerini görüntülemek için başvuruda bulunabilirsiniz.
          </Alert>
        </Stack>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5">Kargo</Typography>
        <Button
          variant="text"
          startIcon={<Iconify icon="eva:play-circle-fill" />}
          sx={{ textTransform: 'none' }}
          onClick={handleOpenVideoModal}
        >
          Tanıtım Videosunu İzle
        </Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <Card elevation={0} sx={{ display: 'flex', alignItems: 'center', p: 2, minHeight: 140, flex: 1, borderRadius: 2, bgcolor: 'background.paper' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2">Günlük Kullanım Adedi (Bugün)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Toplam: <strong>{analyticsData?.totalDailyPostUsage ?? '0'}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ortalama: <strong>{analyticsData?.totalCurrentMonthAvaragePostUsage?.toFixed(2) ?? '0.00'}</strong>
              </Typography>
            </Box>
            <Box sx={{ width: 40, height: 40, borderRadius: 1, background: 'linear-gradient(135deg,#3461D6 0%,#264697 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Iconify icon='eva:layers-outline' width={20} height={20} sx={{ color: '#fff' }} />
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, height: 360, bgcolor: 'background.paper', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom> Haftalık Kargo Hizmeti Hareketleri </Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>
              Toplam Kargo Adedi
            </Typography>
            <ReactApexChart options={monthlyStackOptions} series={hasPermission ? monthlyStackSeries : zeroOutSeries(monthlyStackSeries)} type="bar" height={260} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, height: 360, bgcolor: 'background.paper', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom> Aylık Kargo Hizmeti Kullanımı </Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>
              Firmalara Göre Dağılımı
            </Typography>
            <ReactApexChart options={companyUsageOptions} series={hasPermission ? companyUsageSeries : zeroOutSeries(companyUsageSeries)} type="donut" height={260} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, height: 360, bgcolor: 'background.paper', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom> Günlük Kargo Hizmeti Hareketleri </Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>
              Toplam Kargo Adedi
            </Typography>
            <ReactApexChart options={weeklyStackOptions} series={hasPermission ? weeklyStackSeries : zeroOutSeries(weeklyStackSeries)} type="bar" height={260} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, height: 360, bgcolor: 'background.paper', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom> Aylık Müşteri Sadakati </Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>
              Müşteri Segmentasyonuna Göre Dağılımı
            </Typography>
            <ReactApexChart options={loyaltyOptions} series={hasPermission ? loyaltySeries : zeroOutSeries(loyaltySeries)} type="donut" height={260} />
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={0} sx={{ p: 3, borderRadius: 2, height: 380, bgcolor: 'background.paper' }}>
            <Typography variant="h6">Aylık Kargo Kullanım Hareketi (Son 12 Ay)</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>Firmalara Göre Toplam Kullanım Adedi</Typography>
            <ReactApexChart 
              options={yearlyCompanyUsageChart.options} 
              series={hasPermission ? yearlyCompanyUsageChart.series : zeroOutSeries(yearlyCompanyUsageChart.series)} 
              type="bar" 
              height={280} 
            />
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, mt: 2 }}>
            <Box mb={1}>
              <Typography variant="h6">Gün ve Saat Bazlı Aylık Kargo Hizmeti Kullanımı</Typography>
              <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="body2">
                Saat Aralığı
              </Typography>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <ReactApexChart
                  options={dailyHeatmapChart.options}
                  series={hasPermission ? dailyHeatmapChart.series : zeroOutSeries(dailyHeatmapChart.series)}
                  type="heatmap"
                  height={320}
                />
              </Box>
              <HeatmapLegend
                maxCount={dailyHeatmapChart.maxCount}
                colors={dailyHeatmapChart.colors}
                label="Gönderi"
              />
            </Stack>
          </Card>
        </Grid>
      </Grid>
      
      <Dialog
        open={isVideoModalOpen}
        onClose={handleCloseVideoModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Tanıtım Videosu
          <IconButton
            aria-label="close"
            onClick={handleCloseVideoModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <video
            width="100%"
            height="auto"
            controls
            autoPlay
            src="https://files.easypoint.com.tr/files/data-ekranlari/ep.mp4"
          >
            Tarayıcınız video etiketini desteklemiyor.
          </video>
        </DialogContent>
      </Dialog>
      <Dialog open={openApplyDialog} onClose={handleCloseDialog}>
        <DialogTitle>Hizmet Başvurusu</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bu hizmetle ilgili bilgi alma isteğiniz Genel Merkezimize iletilecektir. Onaylıyor musunuz?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button onClick={handleConfirmApply} autoFocus>Onayla</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}